import i18next from "i18next"
import React, { useEffect, useState } from "react"

import { RootLayout } from "site/rootLayout"
import { authOptions } from "site/rootWidget"
import { routeUpdaters } from "site/routes/routesState"

import * as Api from "subscription/subscriptionApi"
import { SubscriptionState } from "subscription/subscriptionState"
import { DataLayer, EcommerceData, initializePIIData } from "utils/datalayer"

import { AuthButtonPrimary } from "utils/forms"
import { ComponentDidMount } from "utils/lifecycleMethods"
import { getClassName, setIntervalRepetitions } from "utils/utils"

interface SuccessUpgradeSubscriptionLayoutProps {
    userSubscriptionState: SubscriptionState['userSubscriptions']
    onUserSubscriptionStateChange: (state: SubscriptionState['userSubscriptions']) => void
}

const SuccessUpgradeSubscriptionLayout = ({userSubscriptionState, onUserSubscriptionStateChange}: SuccessUpgradeSubscriptionLayoutProps) => {

    if(userSubscriptionState.kind !== 'loaded') {
        return;
    }

    const isSuccesfullyUpgraded: boolean =
        (userSubscriptionState.value.PaywallStatus === 'ValidSubscription' ||
        userSubscriptionState.value.PaywallStatus === 'StillValidTerminatedSubscription');

    let counter: number = 0;
    const maxRetries = 20;

    const [isMaxRetry, setIsMaxRetry] = useState<boolean>(false);

    const dataLayerPush = () => {
        const piiData = initializePIIData(userSubscriptionState);
        const ecommerceData: EcommerceData = {
            transactionId: userSubscriptionState.value.StripeSubscription?.ExternalId || '',
            value: userSubscriptionState.value.StripeSubscription?.Amount ?? 0,
        }
        
        DataLayer.upgradeFlowSuccessfulPayment(
            userSubscriptionState.value.StripeSubscription?.PaymentFrequency ?? '',
            piiData, ecommerceData)
    }

    const checkSuccessfullSubscription = async () => {
        const newUserSubscription = await Api.getUserSubscription();

        if(newUserSubscription.value.PaywallStatus === userSubscriptionState.value.PaywallStatus) {
            setIsMaxRetry(++counter >= maxRetries);
            return;
        }

        const newState = {
            value: {
                ...userSubscriptionState.value,
                PaywallStatus: newUserSubscription.value.PaywallStatus
            }
        }

        onUserSubscriptionStateChange({...userSubscriptionState, ...newState})
    }

    const SuccessUpgradeWidget = () => (
        <>
            <ComponentDidMount onMount={dataLayerPush}/>
            <header
                className={getClassName(authOptions, 'headerClassName', 'mb-4')}
            >
                <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
                {i18next.t('subscription:NewSubscriptionThankYouTitle', {
                    firstName:
                    userSubscriptionState.kind === 'loaded' ? userSubscriptionState.value.User.Name : ''
                })}
                </h1>
                <div
                className={getClassName(authOptions, 'introClassName', 'lead')}
                dangerouslySetInnerHTML={{
                    __html: i18next.t(
                    'subscription:NewSubscriptionThankYouMessage'
                    ),
                }}
                />
            </header>

            <div
                className={getClassName(authOptions, 'formActionsClassName', '')}
            >
                <div
                className={getClassName(
                    authOptions,
                    'clickableWrapperClassName',
                    ''
                )}
                >
                <AuthButtonPrimary
                    kind="link"
                    label={i18next.t('subscription:NewSubscriptionThankYouButton')}
                    disabled={false}
                    to={routeUpdaters(i18next.language).home.url}
                />
                </div>
            </div>

            <footer
                className={getClassName(authOptions, 'footerClassName', 'mt-4')}
            >
                <p
                className={getClassName(
                    authOptions,
                    'footerItemClassName',
                    'mb-1'
                )}
                >
                {i18next.t('auth:assistanceNeededEmailSent')}
                </p>
            </footer>
        </>
    )
    
    const UnsuccessfulUpgradeWidget = () => isMaxRetry 
    ?           
    <header className={getClassName(authOptions, 'headerClassName', 'mb-4')}>
        <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
            {i18next.t('subscription:Oops')}
        </h1>
        <p className={getClassName(authOptions, 'introClassName', 'lead')}>
            {i18next.t('subscription:PaymentErrorMessage')}
        </p>
    </header> 
    : <RootLayout.Loader hasWrapper showText />

    useEffect(() => {
        if(isSuccesfullyUpgraded) {
            return;
        }

        const interval = setIntervalRepetitions(checkSuccessfullSubscription, 1000, maxRetries)

        return () => clearInterval(interval)

    }, [isSuccesfullyUpgraded])

    return (isSuccesfullyUpgraded ? <SuccessUpgradeWidget /> : <UnsuccessfulUpgradeWidget />)
}
export default SuccessUpgradeSubscriptionLayout;
